import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  CreditCard, Smartphone, User, DollarSign, 
  ArrowRight, ShieldCheck, CheckCircle2, 
  Download, Share2, Printer, ArrowLeft, Mail 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { db } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { sealSensitiveDoc } from '../utils/security';
import { validateEmail, validateSLPhone, validateSLNIC, sanitizePhone, sanitizeNIC } from '../utils/validation';

export default function PayOnline() {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const [formData, setFormData] = useState({
    nicNumber: '',
    mobileNumber: '',
    email: '',
    amount: '',
  });

  const [bankSlip, setBankSlip] = useState(null);

  useEffect(() => {
    if (currentUser && userData) {
      setFormData({
        nicNumber: userData?.personal?.nicNumber || '',
        mobileNumber: userData?.personal?.mobileNumber || userData?.phoneNumber || '',
        email: userData?.email || '',
        amount: '',
      });
    }
  }, [currentUser, userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'mobileNumber') {
      setFormData(prev => ({ ...prev, [name]: sanitizePhone(value) }));
      return;
    }

    if (name === 'nicNumber') {
      setFormData(prev => ({ ...prev, [name]: sanitizeNIC(value) }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { nicNumber, mobileNumber, email } = formData;
    
    // Sri Lankan NIC
    if (!validateSLNIC(nicNumber)) {
      alert('Please enter a valid Sri Lankan NIC number (e.g., 940000000V or 199400000000).');
      return false;
    }

    // Sri Lankan Phone Number
    if (!validateSLPhone(mobileNumber)) {
      alert('Please enter a valid Sri Lankan mobile number (e.g., 0712345678).');
      return false;
    }

    // Email validation for guests
    if (!currentUser && !validateEmail(email)) {
      alert('Please enter a valid email address.');
      return false;
    }

    return true;
  };

  const handlePayment = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (!bankSlip) {
      alert('Please upload your bank payment receipt/slip.');
      return;
    }
    setLoading(true);

    // Real submission to Firestore
    const submitRepayment = async () => {
      try {
        const receiptId = `FIN-${Math.floor(100000 + Math.random() * 900000)}`;
        const receipt = {
          receiptId,
          userId: currentUser?.uid || 'guest',
          date: new Date().toLocaleString(),
          nicNumber: formData.nicNumber,
          mobileNumber: formData.mobileNumber,
          email: formData.email,
          amount: Number(formData.amount),
          status: 'pending_admin',
          type: 'repayment',
          paymentMethod: 'Bank Transfer (Slip Uploaded)',
          createdAt: new Date().toISOString()
        };

        // In a real app, we'd upload bankSlip to Cloudinary here as well
        // For now, we store the metadata securely sealed
        await addDoc(collection(db, 'repayments'), sealSensitiveDoc(receipt));
        
        setReceiptData(receipt);
        setSuccess(true);
      } catch (err) {
        console.error(err);
        alert('Submit failed. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    submitRepayment();
  };

  if (success && receiptData) {
    return (
      <div className="min-h-screen bg-[#f6f9fc] flex flex-col items-center justify-center p-6 pb-20">
        <div className="w-full max-w-md bg-white rounded-[32px] shadow-stripe border border-stripe-border overflow-hidden animate-in zoom-in-95 duration-500">
          <div className="bg-amber-500 p-8 text-center text-white relative">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" 
              style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30">
              <ShieldCheck size={40} className="text-white" />
            </div>
            <h2 className="text-2xl font-black tracking-tight">Receipt Received</h2>
            <p className="text-white/80 font-medium text-sm mt-1">
              Our team will verify your bank slip shortly
            </p>
          </div>

          <div className="p-8 space-y-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-stripe-slate font-bold uppercase tracking-widest text-[10px]">Reference</span>
                <span className="text-stripe-dark font-black">{receiptData.receiptId}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-stripe-slate font-bold uppercase tracking-widest text-[10px]">Date</span>
                <span className="text-stripe-dark font-bold">{receiptData.date}</span>
              </div>
              <div className="h-px bg-stripe-border" />
              <div className="flex justify-between items-center text-sm">
                <span className="text-stripe-slate font-bold uppercase tracking-widest text-[10px]">Status</span>
                <span className="font-black text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest bg-amber-100 text-amber-700">
                  {receiptData.status}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-stripe-slate font-bold uppercase tracking-widest text-[10px]">NIC Number</span>
                <span className="text-stripe-dark font-bold">{receiptData.nicNumber}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-stripe-slate font-bold uppercase tracking-widest text-[10px]">Email Address</span>
                <span className="text-stripe-dark font-bold">{receiptData.email || 'N/A'}</span>
              </div>
              <div className="h-px bg-stripe-border" />
              <div className="flex justify-between items-center py-2">
                <span className="text-stripe-dark font-black uppercase tracking-widest text-xs">Total Amount</span>
                <span className="text-2xl font-black text-primary-500">LKR {parseFloat(receiptData.amount).toLocaleString()}</span>
              </div>
            </div>

            <button 
              onClick={() => navigate('/')}
              className="w-full py-4 bg-stripe-dark text-white font-black rounded-xl hover:bg-black transition-all text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} /> Back to Home
            </button>
          </div>
          
          <div className="bg-stripe-bg px-8 py-4 text-center border-t border-stripe-border">
            <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">
              Finova (Pvt) Ltd · Payment Record
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Side: Info */}
          <div className="space-y-8 animate-in fade-in slide-in-from-left-10 duration-700 lg:sticky lg:top-32">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-primary-50 rounded-full border border-primary-100">
              <ShieldCheck size={16} className="text-primary-500" />
              <span className="text-xs font-bold text-primary-600 uppercase tracking-widest">Bank Verification Portal</span>
            </div>
            
            <h1 className="text-5xl font-black text-stripe-dark tracking-tighter leading-tight">
              Submit your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-orange-500">payment receipt.</span>
            </h1>
            
            <p className="text-lg text-stripe-slate font-medium leading-relaxed max-w-xl">
              Pay your dues via direct bank transfer and upload your slip here. Our team will verify and update your balance within 24 hours.
            </p>

            <div className="space-y-4 pt-4">
              {[
                { title: 'Easy Upload', desc: 'Easily upload your bank slip from any device.' },
                { title: 'Manual Verification', desc: 'Deposited funds are manually verified for accuracy.' },
                { title: 'Update Guarantee', desc: 'Once verified, your records update instantly.' }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mt-1 shrink-0">
                    <CheckCircle2 size={14} className="text-primary-500" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stripe-dark">{item.title}</p>
                    <p className="text-xs text-stripe-slate font-medium">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Finova Bank Details */}
            <div className="p-6 bg-stripe-bg border border-stripe-border rounded-[24px] space-y-4 shadow-sm">
              <p className="text-[10px] font-black text-primary-500 uppercase tracking-[0.2em]">Our Payment Details</p>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-xs font-bold text-stripe-slate uppercase">Bank</span>
                  <span className="text-sm font-black text-stripe-dark">Commercial Bank</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs font-bold text-stripe-slate uppercase">Account Name</span>
                  <span className="text-sm font-black text-stripe-dark uppercase">Finova (Pvt) Ltd</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs font-bold text-stripe-slate uppercase">Account No</span>
                  <span className="text-sm font-black text-stripe-dark">1009 2345 6789</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs font-bold text-stripe-slate uppercase">Branch</span>
                  <span className="text-sm font-black text-stripe-dark">Colombo Fort</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Form */}
          <div className="animate-in fade-in slide-in-from-right-10 duration-700 delay-200">
            <div className="bg-white rounded-[32px] border border-stripe-border shadow-2xl p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full -mr-16 -mt-16 blur-3xl opacity-50" />
              
              <div className="mb-8 overflow-hidden">
                <h3 className="text-xl font-black text-stripe-dark tracking-tight">Payment Submission</h3>
                <p className="text-sm text-stripe-slate font-medium mt-1">
                  Once you've made a bank transfer, upload your slip below.
                </p>
              </div>

              <form onSubmit={handlePayment} className="space-y-6">
                {currentUser && userData ? (
                  <div className="p-5 bg-primary-50/50 rounded-2xl border border-primary-100/50 space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-primary-500" />
                        <span className="text-stripe-slate font-bold uppercase tracking-widest text-[9px]">Registered NIC</span>
                      </div>
                      <span className="text-stripe-dark font-black">{userData?.personal?.nicNumber || 'Not verified'}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Smartphone size={14} className="text-primary-500" />
                        <span className="text-stripe-slate font-bold uppercase tracking-widest text-[9px]">Mobile Number</span>
                      </div>
                      <span className="text-stripe-dark font-black">{userData?.personal?.mobileNumber || userData?.phoneNumber}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-primary-500" />
                        <span className="text-stripe-slate font-bold uppercase tracking-widest text-[9px]">Email Address</span>
                      </div>
                      <span className="text-stripe-dark font-black">{userData?.email || 'N/A'}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">NIC Number</label>
                      <div className="relative">
                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                        <input
                          name="nicNumber"
                          type="text"
                          required
                          className="s-input !py-4 !pl-12 !rounded-2xl font-bold uppercase"
                          placeholder="e.g. 199012345678"
                          value={formData.nicNumber}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Mobile Number</label>
                      <div className="relative">
                        <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                        <input
                          name="mobileNumber"
                          type="tel"
                          required
                          className="s-input !py-4 !pl-12 !rounded-2xl font-bold"
                          placeholder="+94 7X XXX XXXX"
                          value={formData.mobileNumber}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Email Address</label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                        <input
                          name="email"
                          type="email"
                          required
                          className="s-input !py-4 !pl-12 !rounded-2xl font-bold"
                          placeholder="yourname@example.com"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Payment Amount (LKR)</label>
                  <div className="relative">
                    <DollarSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                    <input
                      name="amount"
                      type="number"
                      required
                      min="100"
                      className="s-input !py-4 !pl-12 !rounded-2xl font-black text-xl text-primary-500"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                  <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Upload Bank Slip / Receipt</label>
                  <div className="relative">
                    <input 
                      type="file" 
                      id="slip-upload"
                      className="hidden" 
                      accept="image/*"
                      onChange={(e) => setBankSlip(e.target.files[0])}
                    />
                    <label 
                      htmlFor="slip-upload"
                      className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-8 cursor-pointer transition-all ${bankSlip ? 'border-primary-500 bg-primary-50' : 'border-stripe-border hover:border-stripe-slate hover:bg-stripe-bg'}`}
                    >
                      <CreditCard size={32} className={bankSlip ? 'text-primary-500' : 'text-stripe-light'} />
                      <p className="text-xs font-bold text-stripe-dark mt-4">{bankSlip ? bankSlip.name : 'Click to select or take photo'}</p>
                      <p className="text-[10px] text-stripe-slate mt-1">JPEG, PNG allowed</p>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4.5 bg-stripe-dark text-white font-black rounded-2xl hover:bg-black transition-all flex items-center justify-center gap-3 shadow-xl shadow-stripe-dark/10 disabled:opacity-50 group mt-8"
                >
                  {loading ? (
                    'Processing...'
                  ) : (
                    <>
                      Submit Bank Receipt
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

